import type { FC } from "react";

import isNil from "lodash/isNil";

import ApplauseIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/ApplauseIllustrationSVG";
import CheckIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/CheckIllustrationSVG";
import DocumentIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/DocumentIllustrationSVG";
import ExclamationIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/ExclamationIllustrationSVG";
import ExpandIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/ExpandIllustrationSVG";
import EyeIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/EyeIllustrationSVG";
import FlagIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/FlagIllustrationSVG";
import GlassesIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/GlassesIllustrationSVG";
import HomeIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/HomeIllustrationSVG";
import LightBulbIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/LightBulbIllustrationSVG";
import MapMarkerIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/MapMarkerIllustrationSVG";
import MegaphoneIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/MegaphoneIllustrationSVG";
import MessageIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/MessageIllustrationSVG";
import PencilIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/PencilIllustrationSVG";
import ProgressIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/ProgressIllustrationSVG";
import ShieldIllustrationSVG from "@/ui/theme/icons/illustrations/groupClasses/ShieldIllustrationSVG";

type IllustrationDefinition = FC<Partial<SizeDimensions>>;

enum IllustrationSize {
  Default = 48,
  Large = 72,
}

enum IllustrationName {
  Applause = "applause",
  Check = "check",
  Document = "document",
  Exclamation = "exclamation",
  Expand = "expand",
  Eye = "eye",
  Flag = "flag",
  Glasses = "glasses",
  Home = "home",
  Lightbulb = "lightbulb",
  MapMarker = "map_marker",
  Megaphone = "megaphone",
  Message = "message",
  Pencil = "pencil",
  Progress = "progress",
  Shield = "shield",
}

const IllustrationDefinitions: Record<
  IllustrationName,
  IllustrationDefinition
  // eslint-disable-next-line no-restricted-syntax -- definition map keyed by enum
> = {
  [IllustrationName.Applause]: ApplauseIllustrationSVG,
  [IllustrationName.Check]: CheckIllustrationSVG,
  [IllustrationName.Document]: DocumentIllustrationSVG,
  [IllustrationName.Exclamation]: ExclamationIllustrationSVG,
  [IllustrationName.Expand]: ExpandIllustrationSVG,
  [IllustrationName.Eye]: EyeIllustrationSVG,
  [IllustrationName.Flag]: FlagIllustrationSVG,
  [IllustrationName.Glasses]: GlassesIllustrationSVG,
  [IllustrationName.Home]: HomeIllustrationSVG,
  [IllustrationName.Lightbulb]: LightBulbIllustrationSVG,
  [IllustrationName.MapMarker]: MapMarkerIllustrationSVG,
  [IllustrationName.Megaphone]: MegaphoneIllustrationSVG,
  [IllustrationName.Message]: MessageIllustrationSVG,
  [IllustrationName.Pencil]: PencilIllustrationSVG,
  [IllustrationName.Progress]: ProgressIllustrationSVG,
  [IllustrationName.Shield]: ShieldIllustrationSVG,
};

interface GetIllustrationIconProps extends Partial<SizeDimensions> {
  iconName: string;
}

/** Get the requested icon element using a case-insensitive lookup */
const getIllustrationIcon = ({
  height = IllustrationSize.Default,
  iconName,
  width = IllustrationSize.Default,
}: GetIllustrationIconProps) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- legitimate use case
  const name = iconName.toLowerCase() as IllustrationName;
  const Illustration = IllustrationDefinitions[name];
  if (isNil(Illustration)) {
    return null;
  }
  return <Illustration height={height} width={width} />;
};

export { getIllustrationIcon, IllustrationName, IllustrationSize };
export type { GetIllustrationIconProps };
